import type { HireAgentResponsibilities } from "~/lib/onboarding/hire-agent-preferences";
import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import {
  AI_EMPLOYEE_FEATURE_SECTIONS,
  AI_EMPLOYEE_PLANS,
  SITEGLUE_TRIAL_DAYS,
  formatAiEmployeePlanPriceLabel,
} from "~/lib/siteglue/ai-employee-plans";

export { SITEGLUE_TRIAL_DAYS };
import { siteGlueRoleQuestionName } from "~/lib/siteglue/role-builder-prompts";

export const ROLE_CHANNEL_OPTIONS = [
  { id: "live_chat", label: "Live Chat", featureId: "website_chat" },
  { id: "phone_calls", label: "Phone calls", featureId: "phone_calls" },
  { id: "after_hours_phone", label: "After hours phone calls", featureId: "phone_calls" },
  { id: "text_messages", label: "Text Messages", featureId: "sms" },
  { id: "email", label: "Email", featureId: "email_channel" },
  { id: "browser_calls", label: "Browser Calls", featureId: "web_voice" },
  { id: "video_calls", label: "Video Calls", featureId: "video_calls" },
] as const;

export type RoleChannelId = (typeof ROLE_CHANNEL_OPTIONS)[number]["id"];

const ROLE_CHANNEL_IDS = new Set<string>(ROLE_CHANNEL_OPTIONS.map((option) => option.id));

export function parseRoleChannelIds(raw: readonly string[] | null | undefined): RoleChannelId[] {
  if (!raw?.length) return [];
  const seen = new Set<RoleChannelId>();
  for (const value of raw) {
    const id = value.trim();
    if (!ROLE_CHANNEL_IDS.has(id) || seen.has(id as RoleChannelId)) continue;
    seen.add(id as RoleChannelId);
  }
  return ROLE_CHANNEL_OPTIONS.map((option) => option.id).filter((id) => seen.has(id));
}

export type RoleAppointmentsChoice =
  | "juvonno"
  | "nexhealth"
  | "calendly"
  | "google"
  | "later";

const PLAN_ORDER: Array<Exclude<AiEmployeePlanId, "enterprise">> = [
  "intern",
  "associate",
  "professional",
];

export function aiEmployeePlanIncludesFeature(
  planId: AiEmployeePlanId,
  featureId: string,
): boolean {
  for (const section of AI_EMPLOYEE_FEATURE_SECTIONS) {
    const feature = section.features.find((item) => item.id === featureId);
    if (!feature) continue;
    return feature.values[planId] === true;
  }
  return false;
}

export function minimumPlanForRoleChannels(
  channels: RoleChannelId[],
): Exclude<AiEmployeePlanId, "enterprise"> {
  for (const planId of PLAN_ORDER) {
    const covered = channels.every((channelId) => {
      const option = ROLE_CHANNEL_OPTIONS.find((item) => item.id === channelId);
      if (!option) return true;
      return aiEmployeePlanIncludesFeature(planId, option.featureId);
    });
    if (covered) return planId;
  }
  return "professional";
}

export function roleChannelUpgradeNote(
  channelId: RoleChannelId,
  planId: Exclude<AiEmployeePlanId, "enterprise">,
): string | null {
  const option = ROLE_CHANNEL_OPTIONS.find((item) => item.id === channelId);
  if (!option) return null;
  if (aiEmployeePlanIncludesFeature(planId, option.featureId)) return null;
  const required = minimumPlanForRoleChannels([channelId]);
  const name = AI_EMPLOYEE_PLANS.find((plan) => plan.id === required)?.name ?? required;
  return `${option.label} is included on ${name} (${formatAiEmployeePlanPriceLabel(required)}). I'll set the plan to ${name} instead of enabling it on a plan that does not include it.`;
}

export function roleResponsibilityQuestion(roleId: string | null | undefined, roleLabel?: string): string {
  const name = siteGlueRoleQuestionName(roleId, roleLabel);
  return `What is the ${name} responsible for?`;
}

export function roleWebsiteEngagementQuestion(
  roleId: string | null | undefined,
  roleLabel?: string,
): string {
  const name = siteGlueRoleQuestionName(roleId, roleLabel);
  return `How would you like your ${name} to engage with visitors on your website?`;
}

export function roleDedicatedPhoneQuestion(
  roleId: string | null | undefined,
  roleLabel?: string,
): string {
  const name = siteGlueRoleQuestionName(roleId, roleLabel);
  return `Do you want a dedicated phone number for your ${name} to answer phone calls during the day and after hours?`;
}

export function roleEmailInquiryQuestion(
  roleId: string | null | undefined,
  roleLabel?: string,
): string {
  const name = siteGlueRoleQuestionName(roleId, roleLabel);
  return `Do you want your ${name} to respond to email inquiries?`;
}

export const ROLE_WEBSITE_CHANNEL_IDS = ["live_chat", "browser_calls", "video_calls"] as const;

export function channelsFromEngagementAnswers(input: {
  website: RoleChannelId[];
  phone: boolean;
  email: boolean;
}): RoleChannelId[] {
  const website = new Set(ROLE_WEBSITE_CHANNEL_IDS);
  const selected = input.website.filter((id) => website.has(id as (typeof ROLE_WEBSITE_CHANNEL_IDS)[number]));
  if (input.phone) selected.push("phone_calls", "after_hours_phone");
  if (input.email) selected.push("email");
  return parseRoleChannelIds(selected);
}

export function roleChannelsNeedPhone(channels: RoleChannelId[]): boolean {
  return channels.some(
    (id) => id === "phone_calls" || id === "after_hours_phone" || id === "text_messages",
  );
}

export function roleChannelsNeedEmail(channels: RoleChannelId[]): boolean {
  return channels.includes("email");
}

export function responsibilitiesFromRoleSetup(
  draft: Pick<RoleSetupDraft, "channels" | "appointments" | "confirmTeamHandoff">,
): HireAgentResponsibilities {
  const selected = new Set(draft.channels);
  return {
    liveChat: selected.has("live_chat"),
    teamHandoff: draft.confirmTeamHandoff,
    textMessages: selected.has("text_messages"),
    email: selected.has("email"),
    browserCalls: selected.has("browser_calls"),
    phoneCalls: selected.has("phone_calls") || selected.has("after_hours_phone"),
    videoCalls: selected.has("video_calls"),
    welcomeVideo: false,
    messenger: false,
    whatsapp: false,
    appointmentsScheduling: draft.appointments !== "later",
  };
}

export type RoleSetupDraft = {
  channels: RoleChannelId[];
  planId: Exclude<AiEmployeePlanId, "enterprise">;
  agentName: string;
  imageUrl: string;
  trainKnowledge: boolean;
  confirmLocations: boolean;
  confirmUsers: boolean;
  confirmTeamHandoff: boolean;
  confirmExternalActions: boolean;
  appointments: RoleAppointmentsChoice;
  roleLabel: string;
};
